/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  GridCallbackDetails,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { SustainabilityReportResponse } from 'src/shared/services/api/response/sustainabilityReportResponse';
import { useTranslation } from 'react-i18next';
import { useFetchBusinessAreas } from 'src/shared/helpers/hooks/useFetchBusinessAreas';
import { useFetchTags } from 'src/shared/helpers/hooks/useFetchTags';
import { useMobile } from 'src/shared/helpers/hooks/useMobile';
import {
  DataGridPremium,
  DataGridOptions,
} from 'src/shared/components/organisms/dataGridPremium';
import { ProjectStatusEnum } from 'src/shared/enums/projectStatus.enum';
import { CompanyTypeEnum } from 'src/shared/enums/companyType.enum';
import { SustainabilityStatusBadge } from './SustainabilityStatusBadge';
import {
  contributionToSustainabilityOptions,
  likelihoodToWorkAgainOptions,
  satisfactionLevelOptions,
  surveyMethodOptions,
} from '../form/models/sustainnabilityCustomerSurveyOptions';

type SustainabilityDataGridProps = {
  data: SustainabilityReportResponse[];
  loading: boolean;
  totalRows: number;
  onPageChange: (index: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortModelChanged: (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => void | undefined;
  page: number;
  size: number;
  onRowClick: (params: GridRowParams) => void;
  includeCustomerSurveyExport?: boolean;
  toolbarContent?: ReactNode;
};

export const SustainabilityDataGrid = (props: SustainabilityDataGridProps) => {
  const {
    data,
    totalRows,
    onPageChange,
    onPageSizeChange,
    onSortModelChanged,
    page,
    size,
    onRowClick,
    loading,
    includeCustomerSurveyExport = false,
    toolbarContent = undefined,
  } = props;
  const { t } = useTranslation();

  const { isMobileSm, isMobileLg } = useMobile();

  const { businessAreasData } = useFetchBusinessAreas();
  const { tagData } = useFetchTags('Location');

  const hasCustomerSurvey =
    includeCustomerSurveyExport && data.some((row) => row.customerSurvey);

  const surveyMethodLabels = useMemo(
    () =>
      new Map(
        surveyMethodOptions(t).map((option) => [option.value, option.label]),
      ),
    [t],
  );
  const satisfactionLevelLabels = useMemo(
    () =>
      new Map(
        satisfactionLevelOptions(t).map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [t],
  );
  const contributionToSustainabilityLabels = useMemo(
    () =>
      new Map(
        contributionToSustainabilityOptions(t).map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [t],
  );
  const likelihoodToWorkAgainLabels = useMemo(
    () =>
      new Map(
        likelihoodToWorkAgainOptions(t).map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [t],
  );

  const [dataGridOptions, setDataGridOptions] = useState<DataGridOptions>({
    loading,
    rowsPerPageOptions: undefined,
    totalRows,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        valueGetter: (params) => params.row.id,
        hide: true,
      },
      {
        field: 'project',
        headerName: t('project'),
        flex: 1.5,
      },
      {
        field: 'projectNumber',
        headerName: t('nr'),
        hide: true,
        flex: 1,
      },
      {
        field: 'companyName',
        headerName: t('company'),
        sortable: false,
        flex: 1.5,
      },
      {
        field: 'customer',
        headerName: t('customer'),
        hide: isMobileSm,
        flex: 1.5,
      },
      {
        field: 'businessAreaId',
        headerName: t('businessArea'),
        valueGetter: (params) => {
          const businessArea = businessAreasData?.find(
            (area) => area.id === params.row.businessAreaId,
          )?.name;
          return businessArea;
        },
        renderCell: (params) => {
          const businessArea = businessAreasData?.find(
            (area) => area.id === params.row.businessAreaId,
          )?.name;
          return businessArea;
        },
      },
      {
        field: 'locationTagId',
        headerName: t('BusinessArea'),
        valueGetter: (params) => {
          const location = tagData?.find(
            (locationName) => locationName.id === params.row.locationTagId,
          )?.name;
          return location;
        },
        renderCell: (params) => {
          const location = tagData?.find(
            (locationName) => locationName.id === params.row.locationTagId,
          )?.name;
          return location;
        },
      },
      {
        field: 'changed',
        headerName: t('updated'),
        hide: true,
        flex: 1,
        valueGetter: (params) => {
          if (params.row.changed) {
            return dayjs(params.row.changed).format('D MMM YYYY');
          }
          return dayjs(params.row.created).format('D MMM YYYY');
        },
        renderCell: (params) => {
          if (params.row.changed) {
            return dayjs(params.row.changed).format('D MMM YYYY');
          }
          return dayjs(params.row.created).format('D MMM YYYY');
        },
      },
      {
        field: 'projectStatus',
        headerName: t('status'),
        flex: isMobileLg ? 1 : 2,
        valueGetter: (params) => {
          switch (params.row.projectStatus) {
            case ProjectStatusEnum.Approved:
              return t('approved');
            case ProjectStatusEnum.Created:
              return t('reported');
            case ProjectStatusEnum.ReadyForApproval:
              return t('readyForApproval');
            case ProjectStatusEnum.SupplementRequested:
              return t('supplementRequested');
            case ProjectStatusEnum.Denied:
              return t('denied');
            default:
              return '';
          }
        },
        renderCell: (params) => {
          return (
            <SustainabilityStatusBadge status={params.row.projectStatus} />
          );
        },
      },
      {
        field: 'reporterUser.personName',
        valueGetter: (params) => params.row.reporterUser?.personName,
        headerName: t('reportedBy'),
        sortable: false,
        flex: 1.5,
        hide: isMobileSm,
      },
      {
        field: 'reporterUser.email',
        valueGetter: (params) => params.row.reporterUser?.email,
        headerName: t('Email'),
        sortable: false,
        flex: 1.5,
        hide: true,
      },
      {
        field: 'approverUser',
        headerName: t('approverUser'),
        valueGetter: (params) => params.row.approverUser?.personName,
        hide: true,
      },
      {
        field: 'created',
        headerName: t('created'),
        hide: isMobileSm,
        flex: 1,
        valueGetter: (params) => dayjs(params.row.created).format('D MMM YYYY'),
        renderCell: (params) => dayjs(params.row.created).format('D MMM YYYY'),
      },
      {
        field: 'registrationDate',
        headerName: t('registrationDate'),
        hide: true,
        valueGetter: (params) =>
          dayjs(params.row.registrationDate).format('D MMM YYYY'),
        renderCell: (params) =>
          dayjs(params.row.registrationDate).format('D MMM YYYY'),
      },
      {
        field: 'projectEndDate',
        headerName: t('projectEndDate'),
        hide: true,
        valueGetter: (params) =>
          params.row.projectEndDate
            ? dayjs(params.row.projectEndDate).format('D MMM YYYY')
            : '',
        renderCell: (params) =>
          params.row.projectEndDate
            ? dayjs(params.row.projectEndDate).format('D MMM YYYY')
            : '',
      },
      {
        field: 'approvalDate',
        headerName: t('approved'),
        hide: true,
        valueGetter: (params) =>
          dayjs(params.row.approvalDate).format('D MMM YYYY'),
        renderCell: (params) =>
          dayjs(params.row.approvalDate).format('D MMM YYYY'),
      },
      {
        field: 'typeOfCompany',
        headerName: t('typeOfCompany'),
        valueGetter: (params) => {
          switch (params.row.typeOfCompany) {
            case CompanyTypeEnum.Teknikkonsult:
              return t('sustainabilityReportTypeOfCompany.technicalConsult');
            case CompanyTypeEnum.Installation:
              return t('sustainabilityReportTypeOfCompany.installation');
            case CompanyTypeEnum.Industri:
              return t('sustainabilityReportTypeOfCompany.industry');
            default:
              return '';
          }
        },
        renderCell: (params) => {
          switch (params.row.typeOfCompany) {
            case CompanyTypeEnum.Teknikkonsult:
              return t('sustainabilityReportTypeOfCompany.technicalConsult');
            case CompanyTypeEnum.Installation:
              return t('sustainabilityReportTypeOfCompany.installation');
            case CompanyTypeEnum.Industri:
              return t('sustainabilityReportTypeOfCompany.industry');
            default:
              return '';
          }
        },
        hide: true,
      },
      {
        field: 'invoicedAmount',
        headerName: t('invoicedAmount'),
        valueGetter: (params) => params.row.invoicedAmount,
        hide: true,
      },
      {
        field: 'projectDescription',
        headerName: t('projectDescription'),
        valueGetter: (params) => params.row.projectDescription,
        hide: true,
      },
      {
        field: 'climateBenefitDescription',
        headerName: t('climateBenefitDescription'),
        valueGetter: (params) => params.row.climateBenefitDescription,
        hide: true,
      },
      {
        field: 'sustainabilitySolutionTechDescription',
        headerName: t('sustainabilitySolutionTechDescription'),
        valueGetter: (params) =>
          params.row.sustainabilitySolutionTechDescription,
        hide: true,
      },
      ...(hasCustomerSurvey
        ? ([
            {
              field: 'customerSurvey.customerName',
              headerName: t('customerName'),
              valueGetter: (params) =>
                params.row.customerSurvey?.customerName ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.companyName',
              headerName: t('companyName'),
              valueGetter: (params) =>
                params.row.customerSurvey?.companyName ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.surveyMethod',
              headerName: t('surveyMethod'),
              valueGetter: (params) =>
                surveyMethodLabels.get(
                  params.row.customerSurvey?.surveyMethod,
                ) ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.satisfactionLevel',
              headerName: t('satisfactionLevel'),
              valueGetter: (params) =>
                satisfactionLevelLabels.get(
                  params.row.customerSurvey?.satisfactionLevel,
                ) ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.satisfactionLevelComment',
              headerName: t('comment'),
              valueGetter: (params) =>
                params.row.customerSurvey?.satisfactionLevelComment ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.contributionToSustainability',
              headerName: t('contributionToSustainability'),
              valueGetter: (params) =>
                contributionToSustainabilityLabels.get(
                  params.row.customerSurvey?.contributionToSustainability,
                ) ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.contributionToSustainabilityComment',
              headerName: t('comment'),
              valueGetter: (params) =>
                params.row.customerSurvey
                  ?.contributionToSustainabilityComment ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.likelihoodToWorkAgain',
              headerName: t('likelihoodToWorkAgain'),
              valueGetter: (params) =>
                likelihoodToWorkAgainLabels.get(
                  params.row.customerSurvey?.likelihoodToWorkAgain,
                ) ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
            {
              field: 'customerSurvey.likelihoodToWorkAgainComment',
              headerName: t('comment'),
              valueGetter: (params) =>
                params.row.customerSurvey?.likelihoodToWorkAgainComment ?? '',
              hide: true,
              hideable: false,
              disableColumnMenu: true,
            },
          ] as GridColDef[])
        : []),
    ],
    [
      t,
      isMobileSm,
      isMobileLg,
      businessAreasData,
      tagData,
      hasCustomerSurvey,
      surveyMethodLabels,
      satisfactionLevelLabels,
      contributionToSustainabilityLabels,
      likelihoodToWorkAgainLabels,
    ],
  );

  const columnVisibilityModel = useMemo(() => {
    const model: Record<string, boolean> = {};

    columns.forEach((column) => {
      model[column.field] = !column.hide;
    });

    return model;
  }, [columns]);

  useEffect(() => {
    setDataGridOptions((prev) => ({
      ...prev,
      loading,
      totalRows,
    }));
  }, [loading, totalRows]);

  return (
    <>
      <DataGridPremium<SustainabilityReportResponse>
        columns={columns}
        rows={data}
        dataGridOptions={dataGridOptions}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChanged}
        checkboxSelection={false}
        page={page}
        pageSize={size}
        disableSelectionOnClick
        onRowClick={(row) => onRowClick(row)}
        exportName='instalco_export_sustainability'
        columnVisibilityModel={columnVisibilityModel}
        toolbarContent={toolbarContent}
      />
    </>
  );
};
